/*@flow*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import React, { type ElementConfig } from "react";
import { useState, useEffect } from "react";
import Select, { components } from "react-select";
import filterByLabel from "lib/filterByLabel";
import Blank from "lib/Blank";
import * as I from "immutable";
import useCachedQuery from "lib/useCachedQuery";
import getCompanyId from "../../../lib/getCompanyId";
import { GetRecordContacts } from "./queries/GetRecordContacts";
import { GetResourceList } from "../queries/GetResourceList";
import { get } from "lib/t/registry";

export type Option = { value: any, label: string };

const customStyles = {
  input: (base: Object) => ({ ...base, border: "none" }),
  menu: (base: Object) => ({ ...base, maxWidth: 400 }),
  valueContainer: (base: Object) => ({ ...base, paddingLeft: 0 }),
  control: (base: Object) => ({
    ...base,
    border: "none",
    borderBottom: "solid 1px #ccc",
    borderRadius: 0,
    maxWidth: 400,
    boxShadow: "none",
  }),
};

export type PickerProps = {
  id?: ?(ID | ID[]),
  selectProps?: { [key: string]: any },
  onChange: (Option | Option[]) => any,
};

export function mkComponent(props: {
  placeholder: string,
  targetRelation: string,
  isMulti?: boolean,
}) {
  return (nextProps: PickerProps) => {
    return (
      <Resources
        isMulti={props.isMulti}
        placeholder={props.placeholder}
        targetRelation={props.targetRelation}
        id={nextProps.id}
        selectProps={nextProps.selectProps}
        onChange={nextProps.onChange}
      />
    );
  };
}

const DropdownIndicator = (
  props: ElementConfig<typeof components.DropdownIndicator>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="fal fa-map-marker-smile" css={css("color: #666;")} />
    </components.DropdownIndicator>
  );
};

function getNewOptsFilter(data, key) {
  const newOpts = I.getIn(data, ["company", key], []).map((v) => ({
    value: v.id,
    label: v.name,
  }));
  const ids = newOpts.map((v) => String(v.value));
  const filter = (opts) => {
    return opts.filter((v2) => !ids.includes(String(v2.value))).concat(newOpts);
  };
  return { filter };
}

function getSelectedContacts(data, key) {
  const newOpts = I.getIn(data, ["me", "company", key], []).map((v) => ({
    value: v.id,
    label: v.name,
  }));
  const ids = newOpts.map((v) => String(v.value));
  const filter = (opts) => {
    return opts.filter((v2) => !ids.includes(String(v2.value))).concat(newOpts);
  };
  return { filter };
}

export function Resources(props: {
  isMulti?: boolean,
  targetRelation: string,
  placeholder?: string,
  id?: ?(ID | ID[]),
  onChange: (Option | Option[]) => any,
  selectProps?: { [key: string]: any },
}) {
  const {
    isMulti,
    placeholder,
    id,
    onChange,
    selectProps,
    targetRelation,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const handleInputChange = (input) => {
    setInputValue(input);
  };

  const [options, setOptions] = useState([]);
  const companyId = getCompanyId();

  const { data, loading, error } = useCachedQuery(GetRecordContacts, {
    variables: {
      companyId: companyId,
      q: inputValue,
      first: 10,
    },
    skip: Blank.is(companyId),
  });

  const contactIds = Array.isArray(id)
  ? id.filter(Boolean).map((obj) => obj.id || obj)
  : id ? [id.id || id] : [];

  const idSet = useCachedQuery(GetResourceList, {
    variables: {
      id: contactIds,
      resourceName: targetRelation,
    },
    skip: Blank.is(contactIds) || contactIds === null || contactIds.every((id) => id === null),
  });

  useEffect(() => {
    if (loading) return;
    const { filter } = getNewOptsFilter(data, "contacts");
    setOptions((v) => filter(v));
  }, [data, loading]);

  useEffect(() => {
    if (idSet.loading) return;
    const { filter } = getSelectedContacts(idSet.data, "resources");
    setOptions((v) => filter(v));
  }, [idSet.data, idSet.loading]);

  const finder = (opt) => {
    if (Array.isArray(id)) {
      const idValues = id.map((obj) => String(obj.id || obj));
      return idValues.includes(String(opt.value));
    }
    return String(opt.value) === String(id.id || id.value);
  };

  const targetOptionOrOptions = isMulti
    ? options.filter(finder)
    : options.find(finder);

  return (
    <React.Fragment>
      {idSet.error && (
        <div className="text-danger">{idSet.error.toString()}</div>
      )}
      {error && <div className="text-danger">{error.toString()}</div>}
      <Select
        {...selectProps || {}}
        placeholder={placeholder || "Select..."}
        isMulti={isMulti}
        options={options}
        isClearable
        cacheOptions
        components={{ DropdownIndicator }}
        filterOption={filterByLabel}
        onInputChange={handleInputChange}
        onChange={onChange}
        isLoading={loading}
        value={targetOptionOrOptions}
        styles={customStyles}
      />
    </React.Fragment>
  );
}
